<template>
  <div class="desc_editor">
    <label>{{ label }}</label>

    <div class="rows_container">

      <!-- empty -->
      <div v-if="value.length === 0" class="row_container" style="padding-top:1em;">
        None
      </div>

      <!-- grouped entries -->
      <div
          v-for="group in grouped"
          :key="group.payment_id"
          class="group_container"
      >
        <div class="group_label">
          {{ getLabel(group.payment_id) }}
        </div>

        <!-- rows within group -->
        <div
            v-for="row in group.rows"
            :key="row.index"
            class="row_container"
        >
          <b-form-textarea
              ref="descInput"
              class="desc_input"
              v-model="localValues[row.index].description"
              :disabled="disabled"
              @input="emitUpdate(row.index)"
              placeholder="Enter description..."
              style="width:100%;height:50px;"
              tabindex="0"
              @keydown="handleDescKeyDown"
          />

          <b-button
              v-if="!disabled"
              class="del_button"
              size="sm"
              variant="danger"
              style="height: 33px;"
              @click="removeRow(row.index)"
              @keydown="handleDelKeyDown"
          >
            ✕
          </b-button>
        </div>
      </div>

      <!-- ADD FORM -->
      <div v-if="!disabled" class="form">
        <payment_description_input
            ref="inputComponent"
            :payments="payments"
            :value="draft"
            :showAddButton="true"
            @input="updateDraft"
            @add="addRow"
            @advance="advanceFromMiniForm"
            @retreat="retreatFromMiniForm"
            @reset="resetAndFocus"
        />
      </div>

    </div>
  </div>
</template>

<script>
import payment_description_input from './payment_description_input.vue';

export default {
  components: { payment_description_input },

  props: {
    label: { type: String, required: true },
    value: { type: Array, required: true },
    payments: { type: Array, required: true },
    disabled: Boolean
  },

  data() {
    return {
      localValues: JSON.parse(JSON.stringify(this.value)),
      draft: { payment_id: null, description: '' }
    };
  },

  watch: {
    value: {
      deep: true,
      handler(v) {
        this.localValues = JSON.parse(JSON.stringify(v));
      }
    }
  },

  computed: {
    grouped() {
      const map = {};
      this.localValues.forEach((item, index) => {
        if (!map[item.payment_id]) {
          map[item.payment_id] = { payment_id: item.payment_id, rows: [] };
        }
        map[item.payment_id].rows.push({ ...item, index });
      });
      return Object.values(map);
    },
    canAdd() {
      return this.draft.payment_id !== null;
    }
  },

  methods: {
    getLabel(id) {
      const p = this.payments.find(x => x.id === id);
      return p ? `${p.pay_date} — ${p.pay_weekday}` : id;
    },

    getDeleteButtons() {
      return Array.from(this.$el.querySelectorAll('.del_button'));
    },

    updateDraft(v) {
      this.draft = { ...v };
    },

    emitUpdate() {
      const updated = JSON.parse(JSON.stringify(this.localValues));
      this.$emit('input', updated);
    },

    addRow(payload) {
      const toAdd = payload || this.draft;
      const updated = [
        ...this.localValues,
        {
          payment_id: toAdd.payment_id,
          description: (toAdd.description || '').trim()
        }
      ];
      this.$emit('input', updated);
      this.localValues = updated;
      this.resetDraft();
      this.$nextTick(() => this.$refs.inputComponent?.focusPaymentInput());
    },

    removeRow(index) {
      const updated = [...this.localValues];
      updated.splice(index, 1);
      this.$emit('input', updated);
      this.localValues = updated;
    },

    resetDraft() {
      this.draft = { payment_id: null, description: '' };
    },

    resetAndFocus() {
      this.resetDraft();
      this.$nextTick(() => this.$refs.inputComponent?.focusPaymentInput());
    },

    handleDescKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.resetAndFocus();
      }
    },

    advanceFromMiniForm() {
      if (!this.canAdd) this.resetDraft();
      this.$emit('advance');
    },

    retreatFromMiniForm() {
      const buttons = this.getDeleteButtons();
      if (buttons.length) {
        const last = buttons[buttons.length - 1];
        last.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        last.focus?.();
        return;
      }
      if (!this.canAdd) this.resetDraft();
      this.$emit('retreat');
    },

    handleDelKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.resetAndFocus();
        return;
      }

      const buttons = this.getDeleteButtons();
      const idx = buttons.indexOf(e.currentTarget);

      const isBack = e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey);
      const isFwd = e.key === 'ArrowRight' || e.key === 'Tab';

      if (isBack) {
        e.preventDefault();
        if (idx > 0) {
          buttons[idx - 1]?.focus?.();
          return;
        }
        if (!this.canAdd) this.resetDraft();
        this.$emit('retreat');
        return;
      }

      if (isFwd) {
        e.preventDefault();
        if (idx < buttons.length - 1) {
          buttons[idx + 1]?.focus?.();
          return;
        }
        this.$refs.inputComponent?.focusPaymentInput();
      }
    },

    focusFirstDelete() {
      this.$nextTick(() => {
        const buttons = this.getDeleteButtons();
        if (buttons.length) {
          const btn = buttons[0];
          btn.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          btn.focus?.();
        } else {
          this.$refs.inputComponent?.focusPaymentInput();
        }
      });
    },

    focusLastDelete() {
      this.$nextTick(() => {
        const buttons = this.getDeleteButtons();
        if (buttons.length) {
          const btn = buttons[buttons.length - 1];
          btn.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          btn.focus?.();
        } else {
          this.$refs.inputComponent?.focusPaymentInput();
        }
      });
    },

    focusFirstInput() {
      this.$refs.inputComponent?.focusPaymentInput();
    },

    focusPaymentInput() {
      this.$refs.inputComponent?.focusPaymentInput();
    }
  }
};
</script>


<style lang="scss" scoped>
.desc_editor { display:grid; grid-template-columns:auto; }
@media(min-width:1060px){ .desc_editor{ grid-template-columns:200px auto; } }

.desc_editor > label { font-weight:bold; margin-bottom:1em; margin-top:1em; }

.rows_container { margin-bottom:0.5em; }

/* grouping */
.group_container {
  border-left: 3px solid #e6007e;
  padding-left: 10px;
  margin-bottom: 1.5em;
}

.group_label { font-weight:bold; margin-bottom: .5em; }

.row_container {
  display:grid;
  grid-template-columns: 280px 50px;
  grid-gap:10px;
  align-items:center;
  margin-bottom:0.5em;
}

.desc_input { width:100%; padding:3px 6px; }

.form { display:grid; grid-template-columns:350px; grid-row-gap:10px; }

.add_button { height:100%; width:100%; background-color:#d6006e; }
.del_button { height:100%; width:100%; }

.btn:active { color:white; background-color:#d6006e; }
.btn-outline-info:active, .btn-info:hover { color:white !important; background-color:#e6007e !important; }
.btn-info { color:white; background-color:#e6007e; }
.btn-info:disabled { background-color:#ed969e; }
</style>
